import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import retreatService from '../services/retreatService';

const RetreatCenter = () => {
  const navigate = useNavigate();
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countryId, setCountryId] = useState('USA');
  const [selectedCenter, setSelectedCenter] = useState(null);
  const fetchingRef = useRef(false);

  useEffect(() => {
    fetchCenters();
  }, [countryId]);

  const fetchCenters = async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      setLoading(true);
      const data = await retreatService.getCenters(countryId);
      setCenters(data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load centers. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  const handleSelectCenter = (center) => {
    setSelectedCenter(center);
    navigate(`/retreat/selection?centerId=${center.center_Id}&country=${countryId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-16">
      <div className="container-custom">

        {/* Header */}
        <div className="mb-16 text-center">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
              🧘 Retreat Experience
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
            Select Your Retreat Center
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Discover peaceful retreats at our premium centers across the country
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <p className="mt-4 text-gray-600">Loading centers...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
            <button onClick={fetchCenters} className="mt-2 text-sm text-red-600 underline hover:text-red-800 cursor-pointer">
              Try again
            </button>
          </div>
        )}

        {/* Centers Grid */}
        {!loading && centers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {centers.map((center) => (
              <div
                key={center.center_Id}
                className={`item-card cursor-pointer ${selectedCenter?.center_Id === center.center_Id ? 'selected' : ''}`}
                onClick={() => handleSelectCenter(center)}
              >
                {/* Selection Tick */}
                {selectedCenter?.center_Id === center.center_Id && (
                  <div className="absolute top-3 right-3 z-20 w-9 h-9 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg animate-scale-in">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}

                {/* Image Section */}
                {center.center_Logo_URL && (
                  <div className="h-56 bg-gradient-to-br from-blue-400 to-purple-400 overflow-hidden relative rounded-t-[18px]">
                    <img
                      src={center.center_Logo_URL?.trim()}
                      alt={center.center_Name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x300?text=Retreat+Center';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                )}

                {/* Content Section */}
                <div className="p-8 relative z-10">
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
                    {center.center_Name}
                  </h3>

                  <p className="text-gray-600 mb-4 line-clamp-3 text-sm leading-relaxed">
                    {center.center_Desc}
                  </p>

                  {center.camp_Count && (
                    <div className="mb-6 inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold">
                      🧘 {center.camp_Count} retreat{center.camp_Count !== 1 ? 's' : ''} available
                    </div>
                  )}

                  <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-4 rounded-xl transition transform hover:shadow-lg">
                    Explore Retreats →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && centers.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No centers available for the selected country.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetreatCenter;
